import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import debounce from 'lodash/debounce'
import size from 'lodash/size'
import { arrayOf, bool, number, object, shape, string } from 'prop-types'
import { useDispatch } from 'react-redux'
import ReactTooltip from 'react-tooltip'
import { useDeepCompareEffect } from 'react-use'
import { AutoSizer, MultiGrid } from 'react-virtualized'
import { FeatureDict, selectFeatureIncludes } from '@authed/selectors'
import { useIndexedData } from '@components/layout/toolbars/hooks/useIndexedData'
import { SpreadsheetLoader } from '@components/skeletonLoader/spreadsheetLoader'
import {
  DatasetDataContext,
  ActionType as DatasetDataActionType,
} from '@datasets/dataset-details/datasetData/context'
import { DATASET_QUERY_LIMIT } from '@datasets/dataset-query-context'
import { useDatasetQueryContext } from '@datasets/dataset-query-context/hooks'
import { useDatasetPendingRowsObserver } from '@global/hooks/queries/useDatasetPendingRowsObserver'
import { usePasteNotification } from 'handsontable/hooks/usePasteNotification'
import './read-grid.global.css'
import { useAppSelector } from '@redux/hooks'
import { CellRenderer } from './cell-renderers/CellRenderer'
import {
  getUserOptions,
  getCreatedByAndUpdatedByUsers,
} from './columnFilterPopover/selectUser/utils'
import { ReadGridColumnWidthProvider } from './context/column-width-context'
import { ReadGridContextMenuProvider } from './context/context-menu-context'
import { ReadGridToolbarContext } from './context/toolbar'
import { NoRows } from './noRows'
import { Selector } from './selector'
import * as css from './styles'
import { getDataForSelection } from './utils'

const ROW_COUNT_COLUMN_FIELD_ID = -1
const ROW_SELECT_COLUMN_ID = -2
const HEADER_ROW_IDX = 0

const SELECTION_LIMIT = 1000

const rebuildTooltip = debounce(() => ReactTooltip.rebuild(), 200, {
  leading: false,
  trailing: true,
})

export const ReadGrid = ({
  allowRightClick = true,
  columns = null,
  limit,
  loading,
  meta,
  onSectionRendered = () => {},
  rows,
  rowCount = rows?.length,
}) => {
  const { getAllDatasetData, getDatasetId } = useDatasetQueryContext()
  const {
    dispatch: dispatchDatasetData,
    rowMode,
    selectiveEditModeEnabled,
    selectedRows,
    selectedCells,
  } = useContext(DatasetDataContext)
  const { setGridRef, gridRef } = useContext(ReadGridToolbarContext)
  const [multi, setMulti] = useState(selectiveEditModeEnabled)
  const [selectAll, setSelectAll] = useState(false)
  const [shiftKey, setShiftKey] = useState(false)
  const [lastClickedRowIdx, setLastClickedRowIdx] = useState(null)
  const dispatch = useDispatch()
  const uniqueUsers = useRef(null)
  const MAX_SELECTION = rowCount > SELECTION_LIMIT ? SELECTION_LIMIT : meta?.count
  const datasetId = getDatasetId()

  const [
    CREATIVE_CLASSIFICATION_ENABLED = false,
    CHIPS_LIST_ENABLED = false,
    IMAGE_PREVIEW_ENABLED = false,
  ] = useAppSelector(
    selectFeatureIncludes([
      FeatureDict.CREATIVE_CLASSIFICATION,
      FeatureDict.CHIPS_LIST,
      FeatureDict.IMAGE_PREVIEW,
    ]),
  )

  const { pendingRows } = useDatasetPendingRowsObserver({ datasetId })

  const headerRow = useMemo(() => {
    if (!selectiveEditModeEnabled) {
      return [{ fieldId: ROW_COUNT_COLUMN_FIELD_ID }, ...columns]
    }
    return [{ fieldId: ROW_COUNT_COLUMN_FIELD_ID }, { fieldId: ROW_SELECT_COLUMN_ID }, ...columns]
  }, [columns, selectiveEditModeEnabled])

  const { data } = useIndexedData({ rows, columns, headerRow, datasetId })

  // Custom Hook to manage copy and paste.
  const { setCopyStatus } = usePasteNotification({
    dispatch,
  })

  useEffect(() => {
    if (data) {
      uniqueUsers.current = getCreatedByAndUpdatedByUsers(data)
    }
  }, [data])

  useEffect(() => {
    setSelectAll(false)
    dispatchDatasetData({ type: DatasetDataActionType.setSelectedRows, payload: {} })
  }, [selectiveEditModeEnabled])

  // Reset tooltip positions on each rerender to account for resizes and scroll.
  useEffect(() => {
    rebuildTooltip()
  })

  useDeepCompareEffect(() => {
    columns.length > 0 &&
      gridRef &&
      gridRef.current.recomputeGridSize({ rowIndex: 0, columnIndex: 0 })
  }, [columns])

  useDeepCompareEffect(() => {
    if (size(selectedRows) === 0) {
      setSelectAll(false)
    }
  }, [selectedRows])

  useEffect(() => {
    window.addEventListener('keydown', handleUpdateShiftKey)

    window.addEventListener('keyup', handleUpdateShiftKey)

    return () => {
      window.removeEventListener('keydown', handleUpdateShiftKey)
      window.removeEventListener('keyup', handleUpdateShiftKey)
    }

    function handleUpdateShiftKey(e) {
      setShiftKey(e.shiftKey)
    }
  }, [])

  useEffect(() => {
    window.addEventListener('copy', copyEvent)

    return () => {
      window.removeEventListener('copy', copyEvent)
    }

    function copyEvent() {
      if (document.querySelector('[role="presentation"]')) return
      setCopyStatus(true)
    }
  }, [])

  const toggleMulti = () => {
    setMulti(!multi)
  }

  function handleSelectRow(rowIdx) {
    setLastClickedRowIdx(rowIdx)

    if (rowIdx === HEADER_ROW_IDX) {
      setSelectAll(prev => !prev)
      handleSelectAllRows()
      return
    }

    const newSelectedRows = { ...selectedRows }

    if (shiftKey) {
      handleSelectMultipleRows(rowIdx, newSelectedRows)
    } else if (rowIdx in newSelectedRows) {
      delete newSelectedRows[rowIdx]
    } else {
      newSelectedRows[rowIdx] = {
        cvid: getCvidFromInitialRows(rowIdx),
        data: rows[rowIdx - 1]?.data,
      }
    }

    setSelectAll(size(newSelectedRows) === MAX_SELECTION)
    dispatchDatasetData({ type: DatasetDataActionType.setSelectedRows, payload: newSelectedRows })
  }

  function handleSelectMultipleRows(clickedRowIdx, currentlySelectedRows) {
    const max = Math.max(clickedRowIdx, lastClickedRowIdx)
    const min = Math.min(clickedRowIdx, lastClickedRowIdx)
    let rowIndicesToSelect = [...Array(max - min + 1).keys()].map(i => i + min)
    let difference = size(selectedRows) + rowIndicesToSelect.length - MAX_SELECTION

    /** if the first row is already selected, we remove it from rowIndicesToSelect and update diff */
    if (selectedRows[min]) {
      rowIndicesToSelect = rowIndicesToSelect.slice(1)
      difference -= 1
    }

    /** We allow 1000 selected rows and slice the difference */
    if (difference > 0) {
      rowIndicesToSelect = rowIndicesToSelect.slice(0, rowIndicesToSelect.length - difference)
    }

    rowIndicesToSelect.forEach(idx => {
      currentlySelectedRows[idx] = {
        cvid: getCvidFromInitialRows(idx),
        data: rows[idx - 1]?.data,
      }
    })
  }

  function handleSelectAllRows() {
    if (selectAll) {
      dispatchDatasetData({ type: DatasetDataActionType.setSelectedRows, payload: {} })
    } else {
      selectAllRows()
    }

    function selectAllRows() {
      const newSelectedRows = {}
      const updatedIdx = Number(Object.keys(rows)[0])
      let currentRowIdx = updatedIdx + 1
      const endRowIdx = rows.length > limit + updatedIdx ? limit + updatedIdx : rows.length

      while (currentRowIdx <= endRowIdx) {
        const currentRowCvid = getCvidFromInitialRows(currentRowIdx)
        if (!pendingRows.includes(currentRowCvid)) {
          newSelectedRows[currentRowIdx] = {
            cvid: currentRowCvid,
            data: rows[currentRowIdx - 1]?.data,
          }
        }
        currentRowIdx += 1
      }
      dispatchDatasetData({ type: DatasetDataActionType.setSelectedRows, payload: newSelectedRows })
    }
  }

  /**
   * Correct the indices - initialRows does not have a header row so the indices
   * need to be decremented by 1.
   * @param {number} idx
   */
  function getCvidFromInitialRows(idx) {
    return rows[idx - 1]?.cvid
  }

  function scrollToColumn() {}

  function scrollToRow() {}

  function reRender(cells) {
    dispatchDatasetData({ type: DatasetDataActionType.setSelectedCells, payload: cells })
  }

  const getDataForSelectionCallback = useCallback(
    async (selection, selectEntireDataset) => {
      if (getAllDatasetData && rowCount > DATASET_QUERY_LIMIT && selectEntireDataset) {
        const datasetData = await getAllDatasetData({ dsId: meta.id })

        return getDataForSelection({
          data: [datasetData.headers, ...datasetData.rows],
          selection,
          columns,
          multiSelect: selectiveEditModeEnabled,
        })
      }

      return getDataForSelection({
        data,
        selection,
        columns,
        multiSelect: selectiveEditModeEnabled,
      })
    },
    [data, columns, selectiveEditModeEnabled, getAllDatasetData],
  )

  const handleSectionRendered = useCallback(
    onSectionRenderedForSelectorComponent => {
      return (...args) => {
        onSectionRenderedForSelectorComponent(...args)
        onSectionRendered(...args)
      }
    },
    [onSectionRendered],
  )

  const selectedRowIndicesObj = useMemo(() => {
    const ret = selectAll ? { 0: true } : {}
    Object.keys(selectedRows).forEach(key => {
      ret[key] = true
    })

    return ret
  }, [selectAll, selectedRows])

  const userOptions = getUserOptions(uniqueUsers.current) || { createdBy: [], updatedBy: [] }
  /**
   * Add a fake column to the header row to allow for the last column have a
   * "padding-right" allowing the user to widen it.
   */
  const headerRowWithFakeColumn = [
    ...headerRow,
    {
      isLastColumn: true,
    },
  ]

  return (
    <css.ReadGridWrapper className="read-grid scrollfix" data-test-id={!loading && 'read-grid'}>
      {loading ? <SpreadsheetLoader columns={6} rows={30} /> : null}
      <div style={{ height: '100%', opacity: loading ? 0 : 1 }}>
        <Selector
          columnCount={headerRowWithFakeColumn.length - 1}
          isControlled
          mode="cells"
          rowCount={rowCount}
          scrollToColumn={scrollToColumn}
          rerender={reRender}
          getDataForSelection={getDataForSelectionCallback}
          scrollToRow={scrollToRow}
          multi={multi}
          limit={limit}
        >
          {({ onSectionRendered: onRendered }) => (
            <AutoSizer onResize={rebuildTooltip}>
              {({ width, height }) => (
                <ReadGridContextMenuProvider
                  data={data}
                  enabled={allowRightClick}
                  selectiveEditModeEnabled={selectiveEditModeEnabled}
                >
                  <ReadGridColumnWidthProvider
                    multi={selectiveEditModeEnabled}
                    columns={headerRowWithFakeColumn}
                    rowMode={rowMode}
                  >
                    {widthContext => {
                      const fixedColumnCount = selectiveEditModeEnabled ? 2 : 1

                      return (
                        <div style={{ position: 'relative' }}>
                          <MultiGrid
                            id="read-grid"
                            cellRenderer={props => {
                              const cellKey = `key-${props?.rowIndex}-${props?.columnIndex}-${
                                columns?.[props?.columnIndex]?.fieldId
                              }`

                              return (
                                <CellRenderer
                                  {...props}
                                  key={cellKey}
                                  cellKey={cellKey}
                                  copyableColumns={columns}
                                  selectedCells={selectedCells}
                                  userOptions={userOptions}
                                  selectAll={selectAll}
                                  creativeClassicationsEnabled={CREATIVE_CLASSIFICATION_ENABLED}
                                  chipsListEnabled={CHIPS_LIST_ENABLED}
                                  imagePreviewEnabled={IMAGE_PREVIEW_ENABLED}
                                />
                              )
                            }}
                            noContentRenderer={() => (
                              <div
                                style={{
                                  width: widthContext.maxColumnWidth * columns?.length,
                                }}
                              />
                            )}
                            selected={selectedCells}
                            onSectionRendered={handleSectionRendered(onRendered)}
                            data={data}
                            ref={ref => setGridRef(ref)}
                            columns={headerRowWithFakeColumn}
                            columnWidth={({ index }) => {
                              return widthContext.getColumnWidth(
                                index,
                                headerRowWithFakeColumn,
                                selectiveEditModeEnabled,
                              )
                            }}
                            columnCount={headerRowWithFakeColumn.length}
                            fixedColumnCount={fixedColumnCount}
                            fixedRowCount={1}
                            height={height}
                            rowHeight={widthContext.getRowHeight}
                            rowCount={rowCount + 1}
                            overscanRowCount={10}
                            width={width}
                            handleSelect={handleSelectRow}
                            selectedRows={selectedRowIndicesObj}
                            toggleMulti={toggleMulti}
                            multiSelect={selectiveEditModeEnabled}
                            multiSelectEnabled={selectiveEditModeEnabled}
                            enableFixedColumnScroll={true}
                            enableFixedRowScroll={true}
                            hideTopRightGridScrollbar={true}
                            hideBottomLeftGridScrollbar={true}
                            className="gridRoot"
                            classNameTopRightGrid="gridTopRight"
                            classNameTopLeftGrid="gridTopLeft"
                            classNameBottomLeftGrid="gridBottomLeft"
                            classNameBottomRightGrid="gridBottomRight"
                            onScroll={rebuildTooltip}
                          />
                          {!loading && rowCount === 0 && (
                            <NoRows width={width} fixedColumnCount={fixedColumnCount} />
                          )}
                        </div>
                      )
                    }}
                  </ReadGridColumnWidthProvider>
                </ReadGridContextMenuProvider>
              )}
            </AutoSizer>
          )}
        </Selector>
      </div>
      <ReactTooltip className="read-grid-tooltip" />
    </css.ReadGridWrapper>
  )
}

// enforce prop types on some critical properties because they are complex (and different than they used to be)
ReadGrid.propTypes = {
  rows: arrayOf(
    shape({
      cvid: string,
      data: object,
    }),
  ).isRequired,
  columns: arrayOf(
    shape({
      name: string.isRequired,
      fieldId: number.isRequired,
      governanceType: string,
      description: string,
      precedence: number,
      visible: bool,
    }),
  ),
}
